/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

// Utils
import { useTranslation } from 'react-i18next';
import { isNil } from 'lodash';
import { useMediaQuery } from 'react-responsive';
import cn from '../../../utils/tools/cn';

// Icons
import { ReactComponent as EyeIcon } from '../../../assets/icons/view_password.svg';
import { ReactComponent as UpIcon } from '../../../assets/icons/up.svg';
import { ReactComponent as DownIcon } from '../../../assets/icons/down.svg';
import { ReactComponent as CrossIcon } from '../../../assets/icons/cross.svg';

// Components
import FlashMessage from '../../molecules/FlashMessage/FlashMessage';

import styles from './InputField.module.scss';

function InputField({
  id,
  name,
  label,
  value,
  full,
  noLeftBorder,
  className,
  error,
  isRequired,
  textarea,
  disabled,
  maxWidth,
  step,
  disabledBrowserSuggestions,
  clearable,
  minNumberValue,
  maxNumberValue,
  allowNegativeValue,
  formik,
  icon,
  ...props
}) {
  const { t } = useTranslation();
  const isMobile = useMediaQuery({ maxWidth: 768 });

  const [viewPassword, setViewPassword] = useState(false);

  const handleChangeValue = (type) => {
    if (!isNil(value) && formik.setFieldValue) {
      const val = +value;
      formik.setFieldValue(
        name,
        type === 'up'
          ? ((val * 1000) + (step * 1000)) / 1000
          : ((val * 1000) - (step * 1000)) / 1000,
      );
    }
  };

  useEffect(() => {
    if (props.type === 'number') {
      if (value === '') {
        formik.setFieldValue(name, '');
      } else {
        if (value <= 0 && !allowNegativeValue) {
          formik.setFieldValue(name, 0);
        }

        if (minNumberValue && value < minNumberValue) {
          formik.setFieldValue(name, minNumberValue);
        }

        if (maxNumberValue && value > maxNumberValue) {
          formik.setFieldValue(name, maxNumberValue);
        }
      }
    }
  }, [value]);

  return (
    <div className={cn([full ? 'w-100' : '', className || ''])}>
      <div className="d-flex a-stretch">
        {icon ? (
          <span className={cn([styles.icon, 'd-flex center'])}>{icon}</span>
        ) : null}

        <div
          className={cn([
            styles.input_wrapper,
            disabled ? styles.disabled : '',
            textarea ? styles.textarea : '',
            error ? styles.error : '',
            'd-flex a-center flex-auto',
            full ? 'w-100' : '',
            noLeftBorder || icon ? styles.no_left_border : '',
            clearable ? styles.clearable : '',
          ])}
          style={maxWidth && !isMobile ? { maxWidth } : {}}
        >
          {textarea ? (
            <textarea
              id={id}
              name={name}
              placeholder={label}
              value={!isNil(value) ? value : ''}
              className={cn([
                value ? styles.has_value : '',
                props.type === 'number' ? styles.number : '',
              ])}
              disabled={disabled}
              {...props}
            />
          ) : (
            <>
              <input
                id={id}
                name={name}
                placeholder={label}
                value={!isNil(value) ? value : ''}
                className={cn([
                  value ? styles.has_value : '',
                  props.type === 'number' ? styles.number : '',
                ])}
                {...props}
                type={viewPassword ? 'text' : props.type}
                autoComplete={disabledBrowserSuggestions ? (props.type === 'password' ? 'new-password' : 'off') : ''}
                disabled={disabled}
                onChange={formik?.handleChange || props.onChange}
                onBlur={formik?.handleBlur || props.onBlur}
              />
              {clearable && value ? (
                <button
                  type="button"
                  className="d-flex center p-10 c-pointer"
                  onClick={() => formik.setFieldValue(name, '')}
                >
                  <CrossIcon width={20} height={20} className="text-c-grey-600" />
                </button>
              ) : null}
            </>
          )}

          {label ? (
            <label htmlFor={id} className="pointer-none">
              {label}
              {isRequired ? <span>*</span> : ''}
            </label>
          ) : null}

          {props.type === 'number' ? (
            <div className={cn([styles.number_icons, 'd-flex d-col center'])}>
              <button
                type="button"
                className="d-flex center"
                onClick={() => handleChangeValue('up')}
              >
                <UpIcon />
              </button>
              <button
                type="button"
                className="d-flex center"
                onClick={() => handleChangeValue('down')}
              >
                <DownIcon />
              </button>
            </div>
          ) : null}

          {props.type === 'password' ? (
            <button
              type="button"
              className={cn([
                styles.view_pass,
                'd-flex center p-10',
                viewPassword ? styles.active : '',
              ])}
              onClick={() => setViewPassword(!viewPassword)}
            >
              <EyeIcon />
            </button>
          ) : null}
        </div>
      </div>

      {error ? (
        <div id={`error_${name}`} className="m-t-10">
          <FlashMessage message={t(error)} color="error" />
        </div>
      ) : null}
    </div>
  );
}

InputField.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string,
  label: PropTypes.string,
  className: PropTypes.string,
  full: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  type: PropTypes.string,
  error: PropTypes.string,
  noLeftBorder: PropTypes.bool,
  isRequired: PropTypes.bool,
  textarea: PropTypes.bool,
  disabled: PropTypes.bool,
  maxWidth: PropTypes.number,
  step: PropTypes.number,
  disabledBrowserSuggestions: PropTypes.bool,
  clearable: PropTypes.bool,
  minNumberValue: PropTypes.number,
  maxNumberValue: PropTypes.number,
  allowNegativeValue: PropTypes.bool,
  formik: PropTypes.shape({
    handleChange: PropTypes.func,
    handleBlur: PropTypes.func,
    setFieldValue: PropTypes.func,
  }),
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  icon: PropTypes.node,
};

InputField.defaultProps = {
  id: null,
  name: null,
  label: null,
  value: '',
  type: 'text',
  error: null,
  className: null,
  full: false,
  noLeftBorder: false,
  isRequired: false,
  textarea: false,
  disabled: false,
  maxWidth: null,
  step: 1,
  disabledBrowserSuggestions: false,
  clearable: false,
  minNumberValue: null,
  maxNumberValue: null,
  allowNegativeValue: false,
  formik: null,
  onChange: () => {},
  onBlur: () => {},
  icon: null,
};

export default InputField;
